import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

function formatContent(html: string) {
  return html
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-heading font-semibold text-foreground-950 mt-8 mb-3">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">$1</h2>')
    .replace(/^#### (.+)$/gm, '<h4 class="text-base font-heading font-semibold text-foreground-900 mt-6 mb-2">$1</h4>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground-950">$1</strong>')
    .replace(/\n\n/g, '</p><p class="text-sm text-foreground-700 leading-relaxed mb-4">')
    .replace(/^- (.+)$/gm, '<li class="text-sm text-foreground-700 ml-4 mb-2 list-disc">$1</li>');
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const [relatedArticles, setRelatedArticles] = useState<typeof articles>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 樱花视频`;
      setRelatedArticles(
        articles
          .filter((a) => a.id !== article.id && a.category === article.category)
          .slice(0, 3)
      );
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="mt-4 text-foreground-600">文章不存在</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    );
  }

  const contentChunks = article.content.split(/\n\n(?=##)/);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.cover,
    publisher: {
      '@type': 'Organization',
      name: '樱花视频',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-14">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.category, to: `/category/${
                article.category === '日本樱花' ? 'japan-sakura' :
                article.category === '中国樱花' ? 'china-sakura' :
                article.category === '延时摄影' ? 'timelapse' :
                article.category === '旅行Vlog' ? 'travel-vlog' :
                'viewing-guide'
              }` },
              { label: article.title },
            ]}
          />

          <header className="mt-6">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              {article.category}
            </span>
            <h1 className="mt-4 text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>{article.date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? (article.views / 10000).toFixed(1) + '万' : article.views} 阅读
              </span>
            </div>
          </header>

          <figure className="mt-6 rounded-xl overflow-hidden">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full aspect-video object-cover"
            />
          </figure>

          <div className="mt-8">
            <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-4 leading-relaxed">
              <strong className="text-foreground-950">摘要：</strong>{article.summary}
            </p>
          </div>

          <div className="mt-8 article-content">
            {contentChunks.map((chunk, i) => (
              <div
                key={i}
                dangerouslySetInnerHTML={{
                  __html: '<p class="text-sm text-foreground-700 leading-relaxed mb-4">' + formatContent(chunk) + '</p>',
                }}
              />
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
                相关文章推荐
              </h3>
              <div className="space-y-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors group"
                  >
                    <img
                      src={ra.cover}
                      alt={ra.title}
                      className="w-32 h-20 rounded-lg object-cover shrink-0"
                    />
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-500">{ra.date} · {ra.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}